import * as z from "zod";

export const shapefileLayerType = {
  fill: "",
  stroke: "-stroke",
  point: "-point",
  property: "-property",
  label: "-shapefile-label"
};

// Coalesce fallbacks for per-feature style expressions; thickness matches the uploader's 2.0.
export const DEFAULT_SHAPEFILE_THICKNESS = 2;
export const DEFAULT_SHAPEFILE_OPACITY = 1;

export const allShapefileTypes = [
  shapefileLayerType.fill,
  shapefileLayerType.stroke,
  shapefileLayerType.point,
  shapefileLayerType.property,
  shapefileLayerType.label
];

export const ShapefileStyleProp = {
  Color: "color",
  StrokeColor: "strokeColor",
  Thickness: "thickness",
  Opacity: "opacity",
  LineStyle: "lineStyle",
  FillPattern: "fillPattern",
  Name: "name"
} as const;

export type ShapefileStylePropT =
  (typeof ShapefileStyleProp)[keyof typeof ShapefileStyleProp];

export const LineStyleSchema = z.enum(["solid", "dashed", "dotted", "dash-dot"]);

export type LineStyle = z.infer<typeof LineStyleSchema>;

export const DEFAULT_LINE_STYLE: LineStyle = "solid";

export const INVALID_LINE_STYLE_MESSAGE =
  "Invalid line style selected. Please choose Solid, Dashed, Dotted, or Dash-Dot.";

export const INVALID_FILL_PATTERN_MESSAGE =
  "Invalid fill pattern selected. Please choose a valid pattern.";

export const ShapefileStyleUpdateSchema = z.object({
  key: z.nativeEnum(ShapefileStyleProp),
  value: z.union([z.string(), z.number()])
});

export type ShapefileStyleUpdate = z.infer<typeof ShapefileStyleUpdateSchema>;

export const LINE_STYLE_OPTIONS: readonly { value: LineStyle; label: string }[] = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "dash-dot", label: "Dash-Dot" }
] as const;

export const LINE_STYLE_DASH_ARRAYS: Record<LineStyle, Maybe<number[]>> = {
  solid: undefined,
  dashed: [8, 4],
  dotted: [2, 4],
  "dash-dot": [8, 4, 2, 4]
};

export const SOLID_DASH_ARRAY: number[] = [1, 0];

export const STYLE_PROP_TO_GEOM_KEY: Record<ShapefileStylePropT, string> = {
  color: "Color",
  strokeColor: "StrokeColor",
  thickness: "Thickness",
  opacity: "Opacity",
  lineStyle: "LineStyle",
  fillPattern: "FillPattern",
  name: "name"
};

export enum ShapefileSource {
  Org = "org",
  McDan = "mcdan"
}

/** Whether the shapefile source is organization-linked or McDaniel-linked. */
export function isLinkedSource(source: Maybe<string>): boolean {
  return source === ShapefileSource.Org || source === ShapefileSource.McDan;
}

export function isLineStyle(value: Maybe<string>): value is LineStyle {
  return LineStyleSchema.safeParse(value).success;
}

export function normalizeLineStyle(value: Maybe<string>): LineStyle {
  return isLineStyle(value) ? value : DEFAULT_LINE_STYLE;
}

export function getDashArray(lineStyle?: Maybe<string>): number[] {
  return LINE_STYLE_DASH_ARRAYS[normalizeLineStyle(lineStyle)] ?? SOLID_DASH_ARRAY;
}

export function resolveShapefileLineStyle(
  shapefile: { lineStyle?: string; shapefileId?: string },
  layerList: { shapefileId?: string; LineStyle?: Maybe<string> }[]
): Maybe<string> {
  return (
    shapefile.lineStyle ??
    layerList.find((l) => l.shapefileId === shapefile.shapefileId)?.LineStyle ??
    undefined
  );
}

// ── Fill Pattern ─────────────────────────────────────────────────────────

export function resolveShapefileFillPattern(
  shapefile: { fillPattern?: string; shapefileId?: string },
  layerList: { shapefileId?: string; FillPattern?: Maybe<string> }[]
): Maybe<string> {
  return (
    shapefile.fillPattern ??
    layerList.find((l) => l.shapefileId === shapefile.shapefileId)?.FillPattern ??
    undefined
  );
}

export const FillPatternSchema = z.enum([
  "none",
  "forward-slash",
  "back-slash",
  "vertical",
  "horizontal",
  "cross",
  "diagonal-cross",
  "small-circle",
  "large-circle",
  "dots",
  "stars",
  "plus"
]);

export type FillPattern = z.infer<typeof FillPatternSchema>;

export const DEFAULT_FILL_PATTERN: FillPattern = "none";

export const FILL_PATTERN_OPTIONS: readonly {
  value: FillPattern;
  label: string;
  symbol: string;
}[] = [
  { value: "none", label: "None", symbol: "" },
  { value: "forward-slash", label: "Forward Slash", symbol: "//" },
  { value: "back-slash", label: "Back Slash", symbol: "\\\\" },
  { value: "vertical", label: "Vertical", symbol: "||" },
  { value: "horizontal", label: "Horizontal", symbol: "--" },
  { value: "cross", label: "Cross", symbol: "+" },
  { value: "diagonal-cross", label: "Diagonal Cross", symbol: "x" },
  { value: "small-circle", label: "Small Circle", symbol: "o" },
  { value: "large-circle", label: "Large Circle", symbol: "O" },
  { value: "dots", label: "Dots", symbol: ".." },
  { value: "stars", label: "Stars", symbol: "*" },
  { value: "plus", label: "Plus", symbol: "+" }
] as const;

export function isFillPattern(value: Maybe<string>): value is FillPattern {
  return FillPatternSchema.safeParse(value).success;
}

export function normalizeFillPattern(value: Maybe<string>): FillPattern {
  return isFillPattern(value) ? value : DEFAULT_FILL_PATTERN;
}

/** Build the Mapbox image name for a fill-pattern. */
export function getFillPatternImageName(pattern: FillPattern, color: string): string {
  const safeColor = color.replace("#", "");
  return `fill-pattern-${pattern}-${safeColor}`;
}
